<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>

      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'deletedRecordsHome' }"
          marginBottom="5"/>

      <h6 class="element-header">  {{ $t('client.title') }}</h6>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('client.deleted_list') }}
        </h5>

        <div class="row align-items-center justify-content-between">
          <PerPage :values="[10, 25, 50, 100]" @update:perPage="getClients"/>

          <div class="form-group mb-0 col-12 col-md-4 mt-3 mt-md-0">
            <input name="searchTerm"
                   class="form-control"
                   @input="handleDebounceSearch"
                   v-model="filterDebounce.searchTerm"
                   :placeholder="$t('general.button.search')"/>
          </div>
        </div>

        <EmptyState
            v-if="!clients.data?.length"
            icon="fa-trash-can"
            title="Keine gelöschten Einträge gefunden"
            subtitle="Es wurden keine gelöschten Einträge gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>{{ $t('general.client_number') }}</th>
              <th>{{ $t('general.client') }}</th>
              <th>{{ $t('general.address_1') }}</th>
              <th>{{ $t('general.address_2') }}</th>
              <th>{{ $t('general.postal_code') }}</th>
              <th>{{ $t('general.city') }}</th>
              <th>{{ $t('general.location') }}</th>
              <th style=" text-align: right;">
                <a href="#">{{ $t('general.actions') }}</a>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(client) in clients.data" :key="client.id">
              <td>{{ client?.customerNumber }}</td>
              <td>{{ client?.name }}</td>
              <td>{{ client?.addressFirst }}</td>
              <td>{{ client?.addressSecond }}</td>
              <td>{{ client?.zip }}</td>
              <td>{{ client?.city }}</td>
              <td>{{ client?.registrationOffice }}</td>
              <td class="text-right">
                <button class="btn btn-primary ml-2"
                        @click="undeleteModal(client?.id, client.name)">
                  <i class="os-icon os-icon-arrow-left7"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="clients?.pagination?.total_pages > 1"
            :totalPages="clients?.pagination?.total_pages"
            :currentPage="clients?.pagination?.current_page"
            :totalItems="clients?.pagination?.total_items"
            :perPage="clients?.pagination?.items_per_page"
            @pagechanged="getClients"/>
      </div>
    </div>


    <ModalConfirm
        v-model="showModal"
        :confirmationQuestion=confirmModalMessage
        :header="''"
        :onConfirm="undeleteOffice"/>

  </PageTemplate>
</template>


<script setup>
import deletedRecordsService from '@/api/DeletedRecords';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import debounce from '@popperjs/core/lib/utils/debounce';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BackButton from "@/components/button/BackButton.vue";

const { t }               = useI18n();
const clients             = ref([]);
const confirmModalMessage = ref('');
const showModal           = ref(false);
const clientId            = ref('');
const isSearching         = ref(false);
const isLoaded            = ref(false);

const filterDebounce = ref({
  searchTerm: '',
});

const debouncedSearch = debounce(async (query) => {
  await getClients(1, undefined, filterDebounce.value);
}, 600);

const handleDebounceSearch = (event) => {
  debouncedSearch(event.target.value);
};

async function getClients(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = clients.value?.pagination?.items_per_page;

  try {
    const { data } = await deletedRecordsService.getDeletedClientList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : ''),
    });

    clients.value     = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function undeleteModal(id, name) {
  CloseToast();
  clientId.value            = '';
  confirmModalMessage.value = '';
  clientId.value            = id;
  showModal.value           = true;
  confirmModalMessage.value = 'Willst du ' + name + ' wiederherstellen?';
}

async function undeleteOffice() {
  try {
    await deletedRecordsService.undeleteClient(clientId.value);

    clientId.value  = false;
    showModal.value = false;

    await getClients();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getClients(),
  ]);

  isLoaded.value = true;
});


</script>

<style scoped>

</style>